import React, { useCallback, useState, useEffect } from 'react';
import { Table, Space, Tooltip, Row, Col } from 'antd';
import { 
  TableWrapper, 
  OperatorEditModal, 
  B2cOperatorEditModal,  
  B2cEditModal, 
  SeminarEditModal,
  B2cScheduleEditModal, 
  SeminarScheduleEditModal,
  ProjectEditModal, 
  ProjectScheduleEditModal } from 'components/molecules';
import { usePopupManager } from 'react-popup-manager';
import { ColumnsType } from 'antd/lib/table/interface';
import { TotalNumMenu } from 'components/atoms';
import { useLocation, useNavigate } from 'react-router';
import { addNewSearch } from 'utils/utils';
import { PlusSquareOutlined, MinusSquareOutlined, CarryOutOutlined } from '@ant-design/icons';
import { useTableInfo } from 'hooks';
import { INIT_PAGESIZE } from 'commons/constants';
import qs from 'query-string';

interface FormItemProps {
  operatorList: MyOperatorItemDto[] | undefined;  
  searchResult: any;
  hiddenColumns: string[];
  register: boolean;
  refetch: () => void;
}

const OPTable = ({operatorList, searchResult, hiddenColumns, register, refetch}: FormItemProps): React.ReactElement => {
  /* ======================================================
   * variables
   * ====================================================== */
  const navigate = useNavigate();
  const location = useLocation();
  const { open } = usePopupManager();
  const [rowExpaned, setRowExpaned] = useState<number[]>([]);  
  const searchParams = qs.parse(location.search);
  const columns: ColumnsType<MyOperatorItemDto> = [
    {
      title: '번호',
      key: 'index',
      width: '50px',
      align: 'center',
      render: (text, record, index) => {
        return (
          <label>{index+1}</label>
        )
      }       
    },  
    {
      title: '고객사',
      dataIndex: 'company_name',
      key: 'company_name',
      width: '120px',
      align: 'center',
      ellipsis: true,
      sorter: (a, b) => a.company_name.localeCompare(b.company_name),
    },     
    {
      title: '교육장소',
      dataIndex: 'lecture_place_type',
      key: 'lecture_place_type',
      width: '100px',
      align: 'center', 
      ellipsis: true, 
    },
    {
      title: '프로젝트명',
      dataIndex: 'title',
      key: 'title',
      width: 'auto',
      align: 'left', 
      ellipsis: true, 
      sorter: (a, b) => a.title.localeCompare(b.title),
      render: (title, record) => {
        return (
          <a onClick={()=>onClickProject(record)}>{title}</a>
        )
      } 
    },  
    {
      title: '강의요청서',
      dataIndex: 'notion_url',
      key: 'notion_url',
      width: 'auto',
      align: 'center',
      ellipsis: true,
      render: (notion_url, record) => {
        return (
          <a href={notion_url} target='blank'>{notion_url}</a>
        )
      }
    },
    {
      title: '운영일시',
      dataIndex: 'schedule_date',
      key: 'schedule_date',
      width: '150px',
      align: 'center',
      ellipsis: true,
      sorter: (a, b) => a.schedule_date.localeCompare(b.schedule_date),
      render: (schedule_date, record) => {
        return (
          <a onClick={()=>onClickOperatorInfo(record)}>{schedule_date}&nbsp;{(!!record.start_time && !!record.end_time) && (<>{record.start_time}~{record.end_time}</>)} </a>
        )
      }        
    },    
    {
      title: '강사',
      dataIndex: 'lecturer_name',
      key: 'lecturer_name',
      width: '100px',
      align: 'center',
      ellipsis: true,
      sorter: (a, b) => a.lecturer_name.localeCompare(b.lecturer_name),
      render: (lecturer_name, record) => {
        return (
          record.lecturer_name.split(',').map((lt) => {
            const info = lt.split('_');
            return (
              <a key={info[0]} onClick={()=>onClickSchedule({...record, id: Number(info[0])})}>{info[1]}&nbsp;</a>
           )})
        )
      }      
    },   
    {
     title: '교육장소',
     dataIndex: 'lecture_place',
     key: 'lecture_place',
     width: 'auto',
     align: 'left',
     ellipsis: true,
   },   
   {
    title: 'PM',
    key: 'pm',
    dataIndex: 'pm',
    width: '80px',
    align: 'center',
    ellipsis: true,
    sorter: (a, b) => a.pm.localeCompare(b.pm),
  },  
   {
    title: 'CM',
    key: 'cm',
    dataIndex: 'cm',
    width: '80px',
    align: 'center',
    ellipsis: true,   
    sorter: (a, b) => a.cm.localeCompare(b.cm), 
  }, 
  {
    title: '미배정스케줄수',
    key: 'check_count',
    dataIndex: 'check_count',
    width: '100px',
    align: 'center',
    ellipsis: true,    
  },   
  ];

  /* ======================================================
  * events
  * ====================================================== */ 
  const onClickRegister = () => {
    open(B2cEditModal, {
      id: 0, 
      onClose: (isOk)=>{
        if(isOk) {
          refetch();
        }
      }});
    };

 const onChangePageInfo = (pageNo: number, pageSize: number) => {
  navigate({
    ...location,
    search: addNewSearch(location.search, { pageNo, pageSize }),
  });
};  

const onClickSchedule = (item: MyOperatorItemDto) => {
  if (item.project_type === 'b2b') {
    open(ProjectScheduleEditModal, {
      group_id: item.group_id,
      id: item.id,
      onClose: (isOk) => {refetch();},
    }); 
  }else if (item.project_type === 'b2c') {
    if (item.company_name === 'HSG 세미나') {
      open(SeminarScheduleEditModal, {
        group_id: item.group_id,
        id: item.id,
        onClose: (isOk) => {
          if (isOk) {
            refetch();
          }
        },
      }); 
    } else {    
      open(B2cScheduleEditModal, {
        group_id: item.group_id,
        id: item.id,
        onClose: (isOk) => {
          if (isOk) {
            refetch();
          }
        },
      }); 
    }
  }
};

const onClickProject = (item: MyOperatorItemDto) => {
  if (item.project_type === 'b2b') {
    open(ProjectEditModal, {
      id: item.group_id,
      pType: 'confirm',
      onClose: (isOk) => {refetch();},
    }); 
  }else if (item.project_type === 'b2c') {
    if (item.company_name === 'HSG 세미나') {
      open(SeminarEditModal, {
        id: item.group_id,
        onClose: (isOk) => {
          if (isOk) {
            refetch();
          }
        },
      }); 
    } else {
      open(B2cEditModal, {
        id: item.group_id,
        onClose: (isOk) => {
          if (isOk) {
            refetch();
          }
        },
      }); 
    }
  }
}; 

const onClickOperatorInfo = (item: MyOperatorItemDto) => {
  if (item.project_type === 'b2b') {
    open(OperatorEditModal, {
      group_srl: item.group_id,
      schedule_date: item.schedule_date,
      onClose: (isOk, op) => {
        if (isOk) {
          refetch();
        }
      },
    }); 
  } else if (item.project_type === 'b2c') {
    open(B2cOperatorEditModal, {
      group_srl: item.group_id,
      schedule_date: item.schedule_date,
      onClose: (isOk, op) => {
        if (isOk) {
          refetch();
        }
      },
    }); 
  }
};

const onExcelDownload = () => {

}  

  /* ======================================================
  * hooks
  * ====================================================== */
  const { pagination, onTableChange } = useTableInfo({
    pageInfo: {
      pageNo: Number(searchParams.pageNo) || 1,
      pageSize: Number(searchParams.pageSize) || INIT_PAGESIZE,
    },
    datas: operatorList,
    totalCount: searchResult.totalCount,
    onChangePageInfo,
  });

  useEffect(() => {
    setRowExpaned([]);
  }, [searchResult.pageNo]); 

  /* ======================================================
   * render items
   * ====================================================== */
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <TotalNumMenu totalNum={searchResult.totalCount} useRegister={register} useExcelDownload onUseRegister={onClickRegister} onDownloadExcel={onExcelDownload}/>
      <TableWrapper isSearch={!!searchParams.search || !!searchParams.lecturer_type || !!searchParams.lecturer_category}>
        <Table
          tableLayout="fixed"
          dataSource={operatorList}
          columns={columns.filter((item) => hiddenColumns.findIndex((col)=>col === item.key) < 0)}
          pagination={(!!searchResult&&searchResult.pageSize>0)?pagination:false}
          onChange={onTableChange}
          style={{ width: '100%', overflowX: 'auto', minWidth: 600 }}
          className="columnCenterFixTable clickable"
          showSorterTooltip={false}
          rowKey={'id'}
        />
      </TableWrapper> 
    </Space>
  );
};

export default OPTable;
